import { TActionGroups, TToolbarInteraction } from '@fluentui/react-teams/lib/esm/components/Toolbar/Toolbar';
import pick from 'lodash/pick';
import React, { useEffect, useState } from 'react';
import { columnKey, IRow, ITableProps, KBTable, TSelected, TTableInteraction } from './KBTable';
import { AddIcon, Button, Flex, Input, ProviderConsumer, SearchIcon, Text } from '@fluentui/react-northstar';
import { useHistory } from 'react-router-dom';
import { AppKBStateContext } from '../AppKBState';
import { appState, KBData } from '../AppState';
import { ObjectSize } from '../shared/common/Helpers';
import { EmptyData } from '../shared/components/EmptyData';
import { Toolbar } from '../shared/components/Toolbar';
import EmptySVG from './../../svg/empty.svg';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { toolbarButtonColor } from '../tikit/ticketHelper';
import { useTranslation } from 'react-i18next';
import { EnumSortOrderType } from '../tikit/toolbar/CustomViewContextProvider';
import { IColumn } from '@fluentui/react/lib/components/DetailsList';
import KnowledgeDetailList from './KnowledgeDetailList';
import { ADMINISTRATORS, ANALYSTS, KNOWLEDGEAGENT, PRODUCT_LICENSE } from '../shared/utils/constants';

export type TListInteraction = TTableInteraction | TToolbarInteraction;

interface IfEmptyProps {
  header: string;
  subHeader: string;
}

export interface IListProps extends ITableProps {
  emptySelectionActionGroups: TActionGroups;
  filters?: columnKey[];
  filtersSingleSelect?: boolean;
  find?: boolean;
  ifEmpty?: IfEmptyProps;
  onInteraction?: (interaction: TListInteraction) => void;
  templates: Array<any>;
  source: "Editorial" | "Template" | "RequestOffering" | "All";
}

export let KBList = (props: IListProps) => {
  const { t } = useTranslation();
  const tableProps = pick(props, [
    'title',
    'columns',
    'rows',
    'selectable',
    'truncate',
    'onInteraction'
  ]);
  // Row selection and common actions
  const [selectedRows, setSelectedRows] = useState<TSelected>(new Set());
  const [isUserLicensed, setIsUserLicensed] = useState<boolean>(false);
  const [canEditKnowledge, setCanEditKnowledge] = useState<boolean>(false);
  const currentState = appState();

  useEffect(() => {
    (async () => {
      const isLicensed = await getCachedFeature(PRODUCT_LICENSE.AnalystAccess);
      const canEdit = currentState.userRoles.roles.some(r => [ADMINISTRATORS, ANALYSTS, KNOWLEDGEAGENT].includes(r));
      setIsUserLicensed(isLicensed && canEdit);
      setCanEditKnowledge(canEdit);
    })();
  }, []);



  const onSelectedChange = (selected: TSelected) => {
    setSelectedRows(selected);
    return selected;
  };


  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const filterMap: { [filterId: string]: [columnKey, string] } = {};
  const [findQuery, setFindQuery] = useState<RegExp | string | null>(null);
  const handleFindChange = (event: any) => {
    onFindQueryChange(event.target.value);
  };
  const onFindQueryChange = (query: string): string => {
    if (query.length > 0) {
      try {
        setFindQuery(new RegExp(query, 'imu'));
      } catch (_err) {
        setFindQuery(query);
      }
    } else setFindQuery(null);
    return query;
  };

  const rowPassesFilters = (row: IRow): boolean => {
    if (selectedFilters.length === 0) return true;
    else {
      return (
        selectedFilters.findIndex(filterId => {
          const [columnKey, value] = filterMap[filterId];
          return row[columnKey] === value;
        }) > -1
      );
    }
  };

  const rowPassesFind = (row: IRow): boolean => {
    if (findQuery) {
      return (
        Object.keys(row).findIndex(columnKey => {
          const value = row[columnKey];
          return (
            typeof value === 'string' &&
            (typeof findQuery === 'string'
              ? value.includes(findQuery)
              : findQuery.test(value))
          );
        }) > -1
      );
    } else return true;
  };

  const filterBy = (row: IRow): boolean => {
    return rowPassesFilters(row) && rowPassesFind(row);
  };

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const history = useHistory();

  const onToolbarInteraction = (interaction: TToolbarInteraction) => {
    if (interaction.action === 'toggle-menu') {
      setMenuOpen(!menuOpen);
    }
  };

  const [kbQuery, setKBQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState({ orderBy: '', orderType: EnumSortOrderType.asc });

  const sortKB = (column: IColumn) => {
    setSortOrder({
      orderBy: column.key,
      orderType: column.key === sortOrder.orderBy && sortOrder.orderType === EnumSortOrderType.asc ? EnumSortOrderType.desc : EnumSortOrderType.asc
    });
  }
  const orderKB = (a: KBData, b: KBData, by: string, dir: EnumSortOrderType, users: PlatformUser[], templates: any[]): number => {
    let result = 0;
    switch (by) {
      case 'questions':
        result = a.questions.join().localeCompare(b.questions.join());
        break;
      case 'answer':
        const aName = a.answer.match(/^ApplyTemplate:\d+$/gi) && templates.filter(x => `ApplyTemplate:${x.Id}` === a.answer).length > 0 ?
          `Template: ${props.templates.filter(x => `ApplyTemplate:${x.Id}` === a.answer)[0].Name}` : a.answer;
        const bName = b.answer.match(/^ApplyTemplate:\d+$/gi) && templates.filter(x => `ApplyTemplate:${x.Id}` === b.answer).length > 0 ?
          `Template: ${props.templates.filter(x => `ApplyTemplate:${x.Id}` === b.answer)[0].Name}` : b.answer;

        result = aName.localeCompare(bName);
        break;
      case 'order':
        result = (a.enableOffering ? a.order : -1) - (b.enableOffering ? b.order : -1);
        break;
      case 'requestOffering':
      case 'serviceOffering':
        const aBy = (a.enableOffering ? a[by] : "") ?? "";
        const bBy = (b.enableOffering ? b[by] : "") ?? "";
        result = aBy.localeCompare(bBy);
        break;
      case 'createdById':
      case 'modifiedById':
        const aUser = users?.find(u => u.Id == a[by]);
        const bUser = users?.find(u => u.Id == b[by]);

        result = (aUser?.FullName ?? aUser?.UserName ?? "").localeCompare(bUser?.FullName ?? bUser?.UserName ?? "");
        break;
      case 'lastUpdatedDateTime':
        result = new Date(a.lastUpdatedDateTime).getTime() - new Date(b.lastUpdatedDateTime).getTime();
        break;
      default:
        result = (a[by] ?? "").localeCompare(b[by] ?? "");
        break;
    }
    return (dir === EnumSortOrderType.desc) ? result * -1 : result;
  }
  const filterKB = (kb: KBData[], users: PlatformUser[], templates: any[], source: "Editorial" | "Template" | "RequestOffering" | "All") => {
    let filteredKb = kb;
    if (source === "RequestOffering")
      filteredKb = kb.filter(d => d.enableOffering);
    else if (source === "Editorial" || source === "Template")
      filteredKb = kb.filter(d => d.source === source);

    filteredKb = filteredKb.filter(data => {
      let qFilter = data.questions.filter(q =>
        q.toLowerCase().includes(kbQuery.toLowerCase())
      );
      let createdByFullName = users && users.find(u => u.Id == data.createdById) ? users.find(u => u.Id == data.createdById).FullName : null;
      let modifiedByFullName = users && users.find(u => u.Id == data.modifiedById) ? users.find(u => u.Id == data.modifiedById).FullName : null;
      let templateName = templates.find(x => x.Name.toLowerCase().includes(kbQuery.toLowerCase()));
      if (qFilter.length > 0)
        return data;
      if (data.answer.toLowerCase().includes(kbQuery.toLowerCase()))
        return data;
      if (data.status.toLowerCase().includes(kbQuery.toLowerCase().replace(/ /g, '')))
        return data;
      if (createdByFullName?.toLowerCase().includes(kbQuery.toLowerCase()) ?? false)
        return data;
      if (modifiedByFullName?.toLowerCase().includes(kbQuery.toLowerCase()) ?? false)
        return data;
      if (templateName && data.answer.toLowerCase().includes(`:${templateName.Id}`))
        return data;
      if (data.serviceOffering?.toLowerCase().includes(kbQuery.toLowerCase()))
        return data;
      if (data.requestOffering?.toLowerCase().includes(kbQuery.toLowerCase()))
        return data;
    });

    if (sortOrder.orderBy !== "")
      filteredKb = filteredKb.sort((a, b) => orderKB(a, b, sortOrder.orderBy, sortOrder.orderType, users, templates));

    return filteredKb;
  };
  return (
    <AppKBStateContext.Consumer>
      {state => {
        return (
          <ProviderConsumer
            render={globalTheme => (
              <>
                {(!props.hasOwnProperty('title') ||
                  props['title'] == 'Users') && (
                    <Toolbar
                      globalTheme={globalTheme}
                      title={`Settings > ${props['title']}`}
                      onInteraction={onToolbarInteraction}
                      saveText="Update All Users"
                      saveCommand={props['syncAllUsers']}
                      breadcrumbs={props['breadcrumbs']}
                    />
                  )}
                {(!props.hasOwnProperty('title') ||
                  props['title'] == 'Groups') && (
                    <Toolbar
                      globalTheme={globalTheme}
                      title={`Settings > ${props['title']}`}
                      breadcrumbs={props['breadcrumbs']}
                      addNew={true}
                      onInteraction={onToolbarInteraction}
                      addNewContent={
                        <Button
                          disabled={!isUserLicensed}
                          icon={<AddIcon />}
                          text
                          primary
                          content="New Group"
                          onClick={() => {
                            var path = `/settings/groups?id=0`;
                            history.push(path);
                          }}
                          style={{
                            color: toolbarButtonColor(isUserLicensed, globalTheme.siteVariables.colorScheme),
                            height: '52px'
                          }}
                        />
                      }
                    />
                  )}
                {(!props.hasOwnProperty('title') ||
                  props['title'] == 'Tags') && (
                    <Toolbar
                      globalTheme={globalTheme}
                      title={`Settings > ${props['title']}`}
                      breadcrumbs={props['breadcrumbs']}
                      addNew={true}
                      onInteraction={onToolbarInteraction}
                      addNewContent={
                        <Button
                          disabled={!isUserLicensed}
                          icon={<AddIcon />}
                          text
                          primary
                          content="New Tag"
                          onClick={() => {
                            var path = `/settings/tags?id=0`;
                            history.push(path);
                          }}
                          style={{
                            color: toolbarButtonColor(isUserLicensed, globalTheme.siteVariables.colorScheme),
                            height: '52px'
                          }}
                        />
                      }
                    />
                  )}
                <Flex style={{ height: 'calc(100vh - 150px)' }} >
                  <Flex.Item grow styles={{ overflow: 'auto' }}>
                    <div>
                      {props.ifEmpty && (
                        <>
                          {tableProps.title === 'KB' && (
                            <div className={`flex justify-between`}>
                              <div className={`ml-4 mb-2`}>
                                {canEditKnowledge && <div className={`md:hidden`}>
                                  <Button disabled={!isUserLicensed} icon={<AddIcon />} primary size="small" content={<Text size="smallest" content={t('knowledge.add-knowledge')} />} onClick={() => { history.push('/servicecatalog/0'); }} />
                                </div>}
                                {canEditKnowledge && <div className={`hidden md:block`}>
                                  <Button disabled={!isUserLicensed} icon={<AddIcon />} primary content={t('knowledge.add-knowledge')} onClick={() => { history.push('/servicecatalog/0'); }} />
                                </div>}
                              </div>
                              <div className={`mr-4 mb-2`}>
                                <Input icon={<SearchIcon />} inverted placeholder={t('knowledge.search-placeholder')} value={kbQuery} onChange={(e, p) => setKBQuery(p.value)} />
                              </div>
                            </div>)}
                          {ObjectSize(tableProps.rows) === 0 && (
                            <EmptyData
                              headerText={props.ifEmpty.header}
                              subheaderText={props.ifEmpty.subHeader}
                              SVGIcon={<EmptySVG width={200} height={200} />}
                            />
                          )}

                          {ObjectSize(tableProps.rows) > 0 && (
                            <>
                              {tableProps.title !== 'KB' && (
                                <>
                                  <div className={`flex justify-end`}>
                                    <div
                                      style={{
                                        marginRight: '16px',
                                        marginBottom: '16px'
                                      }}
                                    >
                                      <Input
                                        icon={<SearchIcon />}
                                        inverted
                                        placeholder={t('knowledge.search-placeholder')}
                                        onChange={handleFindChange}
                                      />
                                    </div>
                                  </div>
                                  <div className={`mb-5`}>
                                    <KBTable
                                      {...tableProps}
                                      {...{ onSelectedChange, filterBy }}
                                      aria-live="polite"
                                      id="fluentui-teams__list-content"
                                      aria-label="List content"
                                    />
                                  </div>
                                </>
                              )}
                              {tableProps.title === 'KB' && (
                                <div className={`mb-5`}>
                                  <KnowledgeDetailList rows={filterKB(state.knowledgeBase, state.users, props.templates, props.source)} sortOrder={sortOrder} onColumnClick={sortKB} isUserLicensed={isUserLicensed} templates={props.templates} users={state.users} />
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {!props.ifEmpty && (
                        <>
                          <div className={`flex justify-end`}>
                            <div
                              style={{
                                marginRight: '16px',
                                marginBottom: '16px'
                              }}
                            >
                              <Input
                                icon={<SearchIcon />}
                                inverted
                                placeholder={t('knowledge.search-placeholder')}
                                onChange={handleFindChange}
                              />
                            </div>
                          </div>

                          <KBTable
                            {...tableProps}
                            {...{ onSelectedChange, filterBy }}
                            aria-live="polite"
                            id="fluentui-teams__list-content"
                            aria-label="List content"
                          />
                        </>
                      )}
                    </div>
                  </Flex.Item>
                </Flex>
              </>
            )}
          />
        );
      }}
    </AppKBStateContext.Consumer>
  );
};